import { useCallback, useEffect, useRef, useState, type ReactNode, type RefObject } from "react";
import { createPortal } from "react-dom";

/**
 * Portalled tooltip above an icon with a downward caret (ClickUp-style).
 * Flips below the anchor when there is not enough room above (e.g. sidebar
 * close control under the browser chrome).
 * Shared by Sidebar icon chrome and sidebar collapse/reopen controls.
 * pointer-events-none — must not steal hits from the anchor (#865).
 */
export function IconTooltip({
  label,
  anchorRef,
  testId,
}: {
  label: ReactNode;
  anchorRef: RefObject<HTMLElement | null>;
  testId?: string;
}) {
  const [pos, setPos] = useState<{
    top: number;
    left: number;
    placement: "above" | "below";
  } | null>(null);
  const tipRef = useRef<HTMLDivElement>(null);

  const recompute = useCallback(() => {
    const btn = anchorRef.current;
    const tip = tipRef.current;
    if (!btn || !tip) return;
    const r = btn.getBoundingClientRect();
    const tw = tip.offsetWidth;
    const th = tip.offsetHeight;
    const idealLeft = r.left + r.width / 2 - tw / 2;
    const clampedLeft = Math.min(idealLeft, window.innerWidth - tw - 8);
    const gap = 6;
    const placeBelow = r.top < th + gap + 8;
    setPos({
      top: placeBelow ? r.bottom + gap : r.top - gap,
      left: Math.max(8, clampedLeft),
      placement: placeBelow ? "below" : "above",
    });
  }, [anchorRef]);

  useEffect(() => {
    recompute();
  }, [recompute]);

  const caretLeft = anchorRef.current
    ? anchorRef.current.getBoundingClientRect().left +
      anchorRef.current.getBoundingClientRect().width / 2 -
      (pos?.left ?? 0)
    : 0;

  const placement = pos?.placement ?? "above";

  return createPortal(
    <div
      ref={tipRef}
      className="fixed z-[6000] pointer-events-none"
      style={{
        top: pos?.top ?? -9999,
        left: pos?.left ?? -9999,
        transform: placement === "above" ? "translateY(-100%)" : "none",
      }}
      data-testid={testId}
      data-placement={placement}
    >
      {placement === "below" && (
        <div
          className="absolute bottom-full h-0 w-0"
          style={{
            left: caretLeft,
            marginLeft: -5,
            borderLeft: "5px solid transparent",
            borderRight: "5px solid transparent",
            borderBottom: "5px solid rgb(30 41 59)",
          }}
          aria-hidden
        />
      )}
      <div className="rounded-md bg-slate-800 px-2.5 py-1.5 text-xs font-medium text-white whitespace-nowrap shadow-lg">
        {label}
      </div>
      {placement === "above" && (
        <div
          className="absolute top-full h-0 w-0"
          style={{
            left: caretLeft,
            marginLeft: -5,
            borderLeft: "5px solid transparent",
            borderRight: "5px solid transparent",
            borderTop: "5px solid rgb(30 41 59)",
          }}
          aria-hidden
        />
      )}
    </div>,
    document.body,
  );
}
